import { useEffect, useState } from 'react';
import { useMovieStore, useUIStore } from '../../stores';
import './Welcome.css';
import {
  PlusIcon,
  Cross1Icon,
  ChevronRightIcon,
  TrashIcon,
  DotsHorizontalIcon,
  Pencil1Icon,
} from '@radix-ui/react-icons';
import { FilmSlate } from '@phosphor-icons/react';
import { Button, DropdownMenu } from '@radix-ui/themes';
import * as Collapsible from '@radix-ui/react-collapsible';
import { usePost, usePatch, useDelete } from '../../hooks';
import type { MovieResponse, MovieSummary } from '../../types';
import { Logo } from '../Logo';
import { WelcomeSplash } from './WelcomeSplash';
import { useTranslation } from '../../i18n';
import { ConfirmModal } from '../ui/ConfirmModal';

const normalizeDimension = (value: number): number => (value > 0 ? value : 500);

const mergeMovieSummaries = (...candidates: Array<MovieSummary | null | undefined>): MovieSummary | null => {
  const items = candidates.filter((item): item is MovieSummary => !!item);
  if (items.length === 0) return null;

  const merged: MovieSummary = { ...items[0] };
  for (const item of items) {
    if (item.name?.trim()) merged.name = item.name;
    if (item.description?.trim()) merged.description = item.description;
    if (item.width && item.width > 0) merged.width = item.width;
    if (item.height && item.height > 0) merged.height = item.height;
    if (item.fps && item.fps > 0) merged.fps = item.fps;
    if (item.sceneCount != null) merged.sceneCount = item.sceneCount;
    if (item.sceneObjectCount != null) merged.sceneObjectCount = item.sceneObjectCount;
    if (item.layerCount != null) merged.layerCount = item.layerCount;
    if (item.movieId != null) merged.movieId = item.movieId;
    if (item.isSample) merged.isSample = true;
    if (item.lastOpenedAt) merged.lastOpenedAt = item.lastOpenedAt;
  }
  return merged;
};

const isSamePreviewSummary = (a: MovieSummary, b: MovieSummary): boolean =>
  a.id === b.id &&
  (a.movieId ?? a.id) === (b.movieId ?? b.id) &&
  (a.name ?? '') === (b.name ?? '') &&
  (a.description ?? '') === (b.description ?? '') &&
  (a.width ?? 0) === (b.width ?? 0) &&
  (a.height ?? 0) === (b.height ?? 0) &&
  (a.fps ?? 0) === (b.fps ?? 0) &&
  (a.sceneCount ?? 0) === (b.sceneCount ?? 0) &&
  (a.sceneObjectCount ?? 0) === (b.sceneObjectCount ?? 0) &&
  (a.layerCount ?? 0) === (b.layerCount ?? 0) &&
  !!a.isSample === !!b.isSample;

type CreateMovieRequest = {
  name: string;
  width?: number;
  height?: number;
  fps?: number;
};

type OpenMovieRequest = {
  movieId: number;
};

type OpenMovieResponse = {
  id: number;
  copiedFromSample: boolean;
};

type UpdateMovieRequest = {
  name: string;
  description: string;
  width: number;
  height: number;
  fps: number;
};

const getMovieSummaryId = (movie: MovieSummary): number => movie.movieId ?? movie.id;

export function Welcome() {
  const { t } = useTranslation();
  const { welcomeOpen, setWelcomeOpen } = useUIStore();
  const [newMovieName, setNewMovieName] = useState('');
  const [showNewMovieForm, setShowNewMovieForm] = useState(false);
  const [previewMovie, setPreviewMovie] = useState<MovieSummary | null>(null);
  const [editedMovieName, setEditedMovieName] = useState('');
  const [editedMovieDescription, setEditedMovieDescription] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isSavingPreview, setIsSavingPreview] = useState(false);
  const { execute: createMovie } = usePost<MovieResponse, CreateMovieRequest>('/api/movies/create');
  const { execute: openMovie } = usePost<OpenMovieResponse, OpenMovieRequest>('/api/movies/open');
  const {
    userAllMovies,
    setUserAllMovies,
    userRecentMovies,
    setUserRecentMovies,
    sampleMovies,
    setSelectedMovieId,
    deleteMovie,
  } = useMovieStore();

  const previewMovieId = previewMovie ? getMovieSummaryId(previewMovie) : null;
  const canManagePreview = !!previewMovie && !previewMovie.isSample && previewMovieId !== null;
  const updateMovieUrl =
    canManagePreview && previewMovieId ? `/api/movies/${previewMovieId}/update` : null;
  const deleteMovieUrl =
    canManagePreview && previewMovieId ? `/api/movies/${previewMovieId}/delete` : null;
  const { execute: saveMovieToApi, error: saveMovieApiError } = usePatch<
    MovieResponse,
    UpdateMovieRequest
  >(updateMovieUrl);
  const { execute: deleteMovieFromApi } = useDelete<void>(deleteMovieUrl);

  const resolvePreviewMovie = (movie: MovieSummary): MovieSummary => {
    const movieId = getMovieSummaryId(movie);
    const fromAll = userAllMovies.find((item) => getMovieSummaryId(item) === movieId);
    const fromRecent = userRecentMovies.find((item) => getMovieSummaryId(item) === movieId);
    const fromSample = sampleMovies.find((item) => getMovieSummaryId(item) === movieId);
    return mergeMovieSummaries(movie, fromRecent, fromSample, fromAll) ?? movie;
  };

  const applySummaryUpdates = (movieId: number, updates: Partial<MovieSummary>) => {
    const patchList = (list: MovieSummary[]) =>
      list.map((movie) =>
        getMovieSummaryId(movie) === movieId ? { ...movie, ...updates } : movie
      );

    setUserAllMovies(patchList(userAllMovies));
    setUserRecentMovies(patchList(userRecentMovies));
    setPreviewMovie((current) =>
      current && getMovieSummaryId(current) === movieId ? { ...current, ...updates } : current
    );
  };

  const syncPreviewDraft = (movie: MovieSummary) => {
    setEditedMovieName(movie.name ?? '');
    setEditedMovieDescription(movie.description ?? '');
  };

  const handlePreviewMovie = (movie: MovieSummary) => {
    const resolved = resolvePreviewMovie(movie);
    setPreviewMovie(resolved);
    setIsEditing(false);
    syncPreviewDraft(resolved);
    setSaveError('');
  };

  const clearPreview = () => {
    setPreviewMovie(null);
    setIsEditing(false);
    setSaveError('');
  };

  // Keep open preview card in sync when list data refreshes (e.g. after save/fetch).
  useEffect(() => {
    if (!previewMovie || isEditing) return;

    const resolved = resolvePreviewMovie(previewMovie);
    if (isSamePreviewSummary(previewMovie, resolved)) return;

    setPreviewMovie(resolved);
    syncPreviewDraft(resolved);
  }, [previewMovie, isEditing, userAllMovies, userRecentMovies, sampleMovies]);

  const handleStartEditing = () => {
    if (!previewMovie || !canManagePreview) return;
    syncPreviewDraft(previewMovie);
    setSaveError('');
    setIsEditing(true);
  };

  const handleCancelPreviewEdits = () => {
    if (!previewMovie) return;
    setIsEditing(false);
    setSaveError('');
    syncPreviewDraft(previewMovie);
  };

  const handleSavePreviewMovie = async () => {
    if (!previewMovie || previewMovie.isSample || previewMovieId === null) {
      setSaveError(t('movieSelector.errors.idUnresolved'));
      return;
    }

    const trimmedName = editedMovieName.trim();
    if (!trimmedName) {
      setSaveError(t('movieSelector.errors.nameEmpty'));
      return;
    }

    setSaveError('');
    setIsSavingPreview(true);
    const safeWidth = normalizeDimension(previewMovie.width ?? 0);
    const safeHeight = normalizeDimension(previewMovie.height ?? 0);
    const safeFps = previewMovie.fps && previewMovie.fps > 0 ? previewMovie.fps : 30;
    const trimmedDescription = editedMovieDescription.trim();

    try {
      const response = await saveMovieToApi({
        name: trimmedName,
        description: trimmedDescription,
        width: safeWidth,
        height: safeHeight,
        fps: safeFps,
      });

      if (response) {
        applySummaryUpdates(previewMovieId, {
          name: trimmedName,
          description: trimmedDescription,
          width: safeWidth,
          height: safeHeight,
          fps: safeFps,
        });
        setEditedMovieName(trimmedName);
        setEditedMovieDescription(trimmedDescription);
        setIsEditing(false);
        return;
      }

      setSaveError(saveMovieApiError || t('movieSelector.errors.saveFailed'));
    } catch (error) {
      console.error('Failed to save movie:', error);
      setSaveError(
        saveMovieApiError ||
          (error instanceof Error ? error.message : t('movieSelector.errors.saveFailed'))
      );
    } finally {
      setIsSavingPreview(false);
    }
  };

  const handleDeletePreviewMovie = async () => {
    if (!previewMovie || previewMovie.isSample || previewMovieId === null) {
      setSaveError(t('movieSelector.errors.idUnresolved'));
      return;
    }

    await deleteMovieFromApi();
    deleteMovie(previewMovieId);
    clearPreview();
    setIsDeleteConfirmOpen(false);
  };

  const handleOpenPreviewedMovie = async () => {
    if (!previewMovie) return;

    if (previewMovie.isSample) {
      const resolvedMovie = await openMovie({ movieId: previewMovie.id });
      if (resolvedMovie) {
        setSelectedMovieId(resolvedMovie.id);
        setWelcomeOpen(false);
      }
      return;
    }

    setSelectedMovieId(getMovieSummaryId(previewMovie));
    setWelcomeOpen(false);
  };

  const handleCreateNewMovie = async () => {
    if (!newMovieName.trim()) return;

    const newMovie = await createMovie({ name: newMovieName });

    if (newMovie) {
      const movieSummary: MovieSummary = {
        id: newMovie.id,
        name: newMovie.name,
        description: '',
        width: normalizeDimension(newMovie.width),
        height: normalizeDimension(newMovie.height),
        fps: newMovie.fps,
        sceneCount: newMovie.sceneCount ?? 0,
        sceneObjectCount: newMovie.sceneObjectCount ?? 0,
        layerCount: newMovie.layerCount ?? 0,
        isSample: false,
      };
      setUserAllMovies([...userAllMovies, movieSummary]);
      setSelectedMovieId(newMovie.id);
      setWelcomeOpen(false);
    }

    setShowNewMovieForm(false);
    setNewMovieName('');
  };

  const width = previewMovie?.width && previewMovie.width > 0 ? previewMovie.width : null;
  const height = previewMovie?.height && previewMovie.height > 0 ? previewMovie.height : null;
  const fps = previewMovie?.fps && previewMovie.fps > 0 ? previewMovie.fps : null;

  return (
    welcomeOpen && (
    <div className="welcome relative flex items-start justify-start">
      <div className="welcome-content relative z-500 flex flex-col gap-1 m-4 p-4">
        <div className="welcome-header">
          {/* <Button variant="surface" className="welcome-close-button" onClick={() => setWelcomeOpen(false)}>
            <Cross1Icon aria-hidden="true" />
          </Button> */}
        </div>
        <div className="welcome-logo-area">
          <h3 className="text-[var(--color-accent-cyan)] font-thin text-4xl">{t('welcome.title')}</h3>
          <Logo className="huge" />
          <p className="m-2 text-md text-theme-muted">
{t('welcome.subtitle')}
          </p>
        </div>
        <div className="welcome-actions">
            <div className="movie-create-area flex flex-col gap-2 items-start p-2">
              {!showNewMovieForm && (
                <Button
                  variant="soft"
                  size="3"
                  onClick={() => setShowNewMovieForm(true)}
                >
                  <PlusIcon aria-hidden="true" />
                  {t('welcome.createProject')}
                </Button>
              )}
              {showNewMovieForm && (
                <div className="create-movie-form">
                  <div className="create-movie-form-header">
                    <label className="create-movie-form-label">{t('welcome.createProject')}</label>
                  </div>
                  <input
                    type="text"
                    className="welcome-input"
                    placeholder={t('welcome.projectNamePlaceholder')}
                    value={newMovieName}
                    onChange={(e) => setNewMovieName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateNewMovie();
                      } else if (e.key === 'Escape') {
                        setShowNewMovieForm(false);
                        setNewMovieName('');
                      }
                    }}
                    autoFocus
                  />
                  <div className="create-movie-form-actions">
                    <Button variant="soft" size="3" onClick={handleCreateNewMovie}>
                      <PlusIcon aria-hidden="true" />
                      {t('common.create')}
                    </Button>
                    <Button variant="surface" size="3" color="gray" onClick={() => setShowNewMovieForm(false)}>
                      <Cross1Icon aria-hidden="true" />
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
        </div>

        <div className={`welcome-main-row${previewMovie ? ' has-preview' : ''}`}>
          <div className="welcome-lists">
            <div className="flex items-center gap-2">
              <h2 className="text-lg text-cyan-500">{t('welcome.recentlyUsed')}</h2>
              <span className="text-sm text-theme-muted">({userRecentMovies.length})</span>
            </div>
            <div className="recent-movies">
              {userRecentMovies.length === 0 && (
                <p className="welcome-empty-message p-5">{t('welcome.noRecent')}</p>
              )}
              {userRecentMovies.length > 0 && (
                <ul className="welcome-movie-list">
                {userRecentMovies.map((movieSummary) => (
                  <li
                    key={movieSummary.id}
                    className="welcome-movie-item"
                    onClick={() => handlePreviewMovie(movieSummary)}
                  >
                    <FilmSlate className="welcome-movie-icon" aria-hidden="true" />
                    <span className="welcome-movie-name">{movieSummary.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="welcome-movies">
            {userAllMovies.length > 0 && (
              <Collapsible.Root defaultOpen>
                <Collapsible.Trigger className="welcome-collapsible-trigger">
                  <span className="welcome-collapsible-arrow"><ChevronRightIcon /></span>
                  <label className="welcome-section-label">{t('welcome.myProjects')}</label>
                  <span className="text-sm text-theme-muted">({userAllMovies.length})</span>
                </Collapsible.Trigger>
                <Collapsible.Content className="welcome-collapsible-content">
                  <ul className="welcome-movie-list">
                    {userAllMovies.map((movieSummary) => (
                      <li
                        key={movieSummary.id}
                        className="welcome-movie-item"
                        onClick={() => handlePreviewMovie(movieSummary)}
                      >
                        <FilmSlate className="welcome-movie-icon" aria-hidden="true" />
                        <span className="welcome-movie-name">{movieSummary.name}</span>
                      </li>
                    ))}
                  </ul>
                </Collapsible.Content>
              </Collapsible.Root>
            )}
            {sampleMovies.length > 0 && (
              <Collapsible.Root>
                <Collapsible.Trigger className="welcome-collapsible-trigger">
                  <span className="welcome-collapsible-arrow"><ChevronRightIcon /></span>
                  <label className="welcome-section-label">{t('welcome.sampleProjects')}</label>
                  <span className="text-sm text-theme-muted">({sampleMovies.length})</span>
                </Collapsible.Trigger>
                <Collapsible.Content className="welcome-collapsible-content">
                  <ul className="welcome-movie-list">
                    {sampleMovies.map((movieSummary) => (
                      <li
                        key={movieSummary.id}
                        className="welcome-movie-item"
                        onClick={() => handlePreviewMovie(movieSummary)}
                      >
                        <FilmSlate className="welcome-movie-icon" aria-hidden="true" />
                        <span className="welcome-movie-name">{movieSummary.name}</span>
                      </li>
                    ))}
                  </ul>
                </Collapsible.Content>
              </Collapsible.Root>
            )}
              {userRecentMovies.length === 0 && userAllMovies.length === 0 && sampleMovies.length === 0 && (
                <div className="welcome-empty-state">
                  <p className="welcome-empty-message">{t('welcome.empty')}</p>
                </div>
              )}
            </div>
          </div>

          {previewMovie && (
            <aside className="welcome-preview">
              <div className="welcome-preview-header">
                <span className="welcome-preview-title">{t('welcome.previewTitle')}</span>
                <div className="welcome-preview-header-actions">
                  {canManagePreview && (
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger>
                        <Button
                          variant="ghost"
                          size="1"
                          className="welcome-preview-more-button"
                          aria-label={t('movieSelector.moreActions')}
                        >
                          <DotsHorizontalIcon aria-hidden="true" />
                        </Button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content variant="soft" size="2" align="end" sideOffset={4}>
                        <DropdownMenu.Item onSelect={handleStartEditing}>
                          <Pencil1Icon aria-hidden="true" />
                          {t('common.edit')}
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          color="red"
                          onSelect={() => setIsDeleteConfirmOpen(true)}
                        >
                          <TrashIcon aria-hidden="true" />
                          {t('movieSelector.deleteMovie')}
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </DropdownMenu.Root>
                  )}
                  <Button
                    variant="ghost"
                    size="1"
                    className="welcome-preview-close"
                    onClick={clearPreview}
                    aria-label={t('common.close')}
                  >
                    <Cross1Icon aria-hidden="true" />
                  </Button>
                </div>
              </div>
              <div className="welcome-preview-body">
                <div className="welcome-preview-identity">
                  <FilmSlate className="welcome-preview-icon" aria-hidden="true" />
                  <div className="welcome-preview-text">
                    {isEditing && canManagePreview ? (
                      <div className="welcome-preview-edit-fields">
                        <input
                          type="text"
                          className="welcome-input"
                          value={editedMovieName}
                          onChange={(e) => setEditedMovieName(e.target.value)}
                          placeholder={t('welcome.projectNamePlaceholder')}
                          aria-label={t('welcome.projectNamePlaceholder')}
                          autoFocus
                        />
                        <textarea
                          className="welcome-input welcome-preview-description-input"
                          value={editedMovieDescription}
                          onChange={(e) => setEditedMovieDescription(e.target.value)}
                          placeholder={t('movieSelector.movieDescriptionPlaceholder')}
                          rows={4}
                          aria-label={t('movieSelector.movieDescriptionPlaceholder')}
                        />
                      </div>
                    ) : (
                      <>
                        <h3 className="welcome-preview-name">{previewMovie.name}</h3>
                        {previewMovie.isSample && (
                          <span className="welcome-preview-badge">{t('welcome.sampleBadge')}</span>
                        )}
                        <p className="welcome-preview-description">
                          {previewMovie.description?.trim()
                            ? previewMovie.description
                            : t('welcome.noDescription')}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                {saveError && <div className="welcome-preview-save-error">{saveError}</div>}
                <dl className="welcome-preview-meta">
                  {(width !== null || height !== null) && (
                    <div className="welcome-preview-meta-row">
                      <dt>{t('welcome.resolution')}</dt>
                      <dd>
                        {width ?? '—'} × {height ?? '—'}
                      </dd>
                    </div>
                  )}
                  {fps !== null && (
                    <div className="welcome-preview-meta-row">
                      <dt>{t('welcome.fps')}</dt>
                      <dd>{t('welcome.fpsValue', { fps })}</dd>
                    </div>
                  )}
                  <div className="welcome-preview-meta-row">
                    <dt>{t('welcome.scenes')}</dt>
                    <dd>{previewMovie.sceneCount ?? 0}</dd>
                  </div>
                  <div className="welcome-preview-meta-row">
                    <dt>{t('welcome.sceneObjects')}</dt>
                    <dd>{previewMovie.sceneObjectCount ?? 0}</dd>
                  </div>
                  <div className="welcome-preview-meta-row">
                    <dt>{t('welcome.layers')}</dt>
                    <dd>{previewMovie.layerCount ?? 0}</dd>
                  </div>
                </dl>
              </div>
              <div className="welcome-preview-actions">
                {isEditing && canManagePreview ? (
                  <>
                    <Button
                      variant="soft"
                      size="3"
                      disabled={isSavingPreview}
                      onClick={() => void handleSavePreviewMovie()}
                    >
                      {t('common.save')}
                    </Button>
                    <Button
                      variant="surface"
                      size="3"
                      color="gray"
                      disabled={isSavingPreview}
                      onClick={handleCancelPreviewEdits}
                    >
                      {t('common.cancel')}
                    </Button>
                  </>
                ) : (
                  <Button variant="solid" size="3" onClick={() => void handleOpenPreviewedMovie()}>
                    {t('welcome.openProject')}
                  </Button>
                )}
              </div>
            </aside>
          )}
        </div>
      </div>

      <ConfirmModal
        open={isDeleteConfirmOpen}
        onOpenChange={setIsDeleteConfirmOpen}
        title={t('movieSelector.deleteConfirmTitle')}
        description={t('movieSelector.deleteConfirmDescription', {
          name: previewMovie?.name ?? t('movieSelector.thisMovie'),
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={() => void handleDeletePreviewMovie()}
      />

      <WelcomeSplash />
    </div>
    )
  );
}
